import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toastr } from "react-redux-toastr";
import { Col, Form, Row} from "react-bootstrap";
import RangeSlider from "react-range-slider-input";

import CustomButton from "../../CustomButton";
import { createZone, editZone } from "../../../action/CompaniesActions";

import "react-range-slider-input/dist/style.css";

export default function ZoneForm (props) {
    const { editedZone, mode, setMode, className, getZones, hasZones, map, mapState, behavior, platform, zoneNames } = props;
    const defaultErrors = {
        zoneName: '',
        address: '',
        latitude: '',
        longitude: '',
        radius: ''
    };
    const { t } = useTranslation('translation', { keyPrefix: 'zonesPage' });
    const user = useSelector(state => state.user.user);
    const company = useSelector(state => state.company.company);
    const companyId = company.company_id;
    const editedZoneGroupRef = useRef(null);

    const [values, setValues] = useState(() => {
        if (mode === 'EDIT' && editedZone) {
            const { zone_name, address, lat, lon, radius } = editedZone;
            return {
                zoneName: zone_name || '',
                address: address || '',
                latitude: lat || '',
                longitude: lon || '',
                radius: radius || 0.01
            };
        }
        return {
            zoneName: '',
            address: '',
            latitude: mapState.lat,
            longitude: mapState.lng,
            radius: 0.01
        };
    });

    const [errors, setErrors] = useState({...defaultErrors});
    const [suggestions, setSuggestions] = useState([]);

    const k = (v, radius) => Math.round(v / 2 + radius * v / 10);

    useEffect(() => {
        if (!map || !behavior) return;

        if (mode === 'EDIT') {
            const allZonesGroup = map.getObjects().find(
                obj => obj instanceof H.map.Group && obj.getRemoteId() === 'allZonesObject'
            );
            if (allZonesGroup) {
                const zoneGroup = allZonesGroup.getObjects().find(
                    obj => obj instanceof H.map.Group && obj.getRemoteId() === editedZone.id
                );
                if (zoneGroup) {
                    editedZoneGroupRef.current = cloneZoneGroup(zoneGroup);
                    allZonesGroup.removeObject(zoneGroup);
                }
            }
        }

        const existing = map.getObjects().filter(obj =>
            obj instanceof H.map.Group && obj.getRemoteId() === 'dragableZoneObject'
        );
        map.removeObjects(existing);

        const dragableZoneObject = new H.map.Group({ volatility: true });
        dragableZoneObject.setRemoteId('dragableZoneObject');
        map.addObject(dragableZoneObject);

        const div = document.createElement('div');
        const label = document.createElement('label');
        div.setAttribute('class', 'markerZone');

        label.style.fontSize = `${k(18, values.radius)}px`;
        label.style.marginTop = `${k(25, values.radius)}px`;
        label.innerText = values.zoneName;
        div.appendChild(label);

        const position = { lat: values.latitude, lng: values.longitude }

        const iconConfig = {
            url: '/images/markerZone.svg',
            size: { w: k(38, values.radius), h: k(55, values.radius) }
        };

        const marker = new H.map.Marker(position, {
            icon: new H.map.Icon(iconConfig.url, { size: iconConfig.size }),
            volatility: true
        });
        marker.draggable = true;

        const textLabel = new H.map.DomMarker(position, {
            icon: new H.map.DomIcon(div)
        });

        const radiusMeters = values.radius * 1609.34;
        const circleShadow = new H.map.Circle(position, radiusMeters, {
            style: { lineWidth: 35, strokeColor: "rgba(74, 168, 255, 0.15)", fillColor: "transparent" }
        });
        const circleBlue = new H.map.Circle(position, radiusMeters, {
            style: { lineWidth: 12, strokeColor: "rgba(126, 200, 255, 0.8)", fillColor: "rgba(74, 168, 255, 0.15)" }
        });
        const circleDashed = new H.map.Circle(position, radiusMeters, {
            style: { lineWidth: 5, lineDash: [4, 4], strokeColor: "rgba(76, 125, 251, 0.95)", fillColor: "transparent" }
        });
        dragableZoneObject.addObjects([marker, textLabel, circleShadow, circleBlue, circleDashed]);

        const handleDragStart = ev => {
            if (ev.target !== marker) return;
            const pointer = ev.currentPointer;
            const pos = map.geoToScreen(marker.getGeometry());
            marker.offset = new H.math.Point(pointer.viewportX - pos.x, pointer.viewportY - pos.y);
            map.getViewPort().element.style.cursor = 'grabbing';
            behavior.disable();
        };

        const handleDrag = ev => {
            if (ev.target !== marker || !marker.offset) return;
            const pointer = ev.currentPointer;
            const newGeo = map.screenToGeo(
                pointer.viewportX - marker.offset.x,
                pointer.viewportY - marker.offset.y
            );
            marker.setGeometry(newGeo);
            textLabel.setGeometry(newGeo);
            circleBlue.setCenter(newGeo);
            circleShadow.setCenter(newGeo);
            circleDashed.setCenter(newGeo);
        };

        const handleDragEnd = ev => {
            if (ev.target !== marker) return;
            map.getViewPort().element.style.cursor = '';
            behavior.enable();
            const newGeo = marker.getGeometry();
            setValues(prev => ({
                ...prev,
                latitude: newGeo.lat,
                longitude: newGeo.lng
            }));
        };

        const handleMapClick = (ev) => {
            const clickedGeo = ev.target instanceof H.map.Marker
                ? ev.target.getGeometry()
                : ev.currentPointer && map.screenToGeo(ev.currentPointer.viewportX, ev.currentPointer.viewportY);

            if (!clickedGeo) return;

            setValues(prev => ({
                ...prev,
                latitude: clickedGeo.lat,
                longitude: clickedGeo.lng
            }));
        };

        map.addEventListener('dragstart', handleDragStart, false);
        map.addEventListener('drag', handleDrag, false);
        map.addEventListener('dragend', handleDragEnd, false);
        map.addEventListener('tap', handleMapClick, false);

        return () => {
            map.removeEventListener('dragstart', handleDragStart);
            map.removeEventListener('drag', handleDrag);
            map.removeEventListener('dragend', handleDragEnd);
            map.removeEventListener('tap', handleMapClick);
        };
    }, [map, behavior, values]);

    useEffect(() => {
        setAddressByCoords(values.latitude, values.longitude);
    }, [values.latitude, values.longitude]);

    const setAddressByCoords = (lat, lng) => {
        const searchService = platform.getSearchService();
        searchService.reverseGeocode(
            {
                at: `${lat},${lng}`,
                limit: 1
            },
            result => {
                const location = result.items?.[0];
                setValues(prev => ({
                    ...prev,
                    address: location ? location.address.label : '',
                }));
            },
            error => {
                console.error("Geocoding error:", error);
            }
        );
    }

    const resetMap = () => {
        if(!map) return;

        const dragableZone = map.getObjects().filter(obj =>
            obj instanceof H.map.Group && obj.getRemoteId() === 'dragableZoneObject'
        );
        map.removeObjects(dragableZone);
    }

    const changeStyle = (circleOutline) => {
        circleOutline.setVisibility(true);

        circleOutline.setStyle({
            lineWidth: 20,
            strokeColor: "rgba(255, 215, 0, 0.25)"
        });

        setTimeout(() => {
            setTimeout(() => {
                fadeOutCircle(circleOutline, 2000);
            }, 3000);
        }, 50);
    };

    const fadeOutCircle = (circleOutline, duration = 2000) => {
        const start = performance.now();
        const initialAlpha = 0.25;

        const animate = (time) => {
            const elapsed = time - start;
            const progress = Math.min(elapsed / duration, 1);
            const newAlpha = initialAlpha * (1 - progress);

            circleOutline.setStyle({
                lineWidth: 20,
                strokeColor: `rgba(255, 215, 0, ${newAlpha})`
            });

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                circleOutline.setVisibility(false);
            }
        };

        requestAnimationFrame(animate);
    };

    const cloneZoneGroup = (zoneGroup) => {
        const group = new H.map.Group();
        group .setRemoteId(zoneGroup.getRemoteId());

        const clonedObjects = [];
        let clonedCircleOutline = null;

        zoneGroup.getObjects().forEach(obj => {
            let copy;

            if (obj instanceof H.map.Circle) {
                copy = new H.map.Circle(obj.getCenter(), obj.getRadius(), {
                    style: { ...obj.getStyle() }
                });
            }

            if (obj instanceof H.map.Polyline) {
                const originalGeometry = obj.getGeometry();
                const clonedGeometry = new H.geo.LineString();

                for (let i = 0; i < originalGeometry.getPointCount(); i++) {
                    clonedGeometry.pushPoint(originalGeometry.extractPoint(i));
                }

                copy = new H.map.Polyline(clonedGeometry, {
                    style: { ...obj.getStyle() }
                });
                clonedCircleOutline = copy;
            }

            if (obj instanceof H.map.DomMarker) {
                const originalElement = obj.getData()?.domElement;
                if (originalElement) {
                    const clonedElement = originalElement.cloneNode(true);
                    copy = new H.map.DomMarker(obj.getGeometry(), {
                        icon: new H.map.DomIcon(clonedElement)
                    });
                }
            }

            if (obj instanceof H.map.Marker) {

                const iconConfig = {
                    url: '/images/markerZone.svg',
                    size: { w: k(38, values.radius), h: k(55, values.radius) }
                };

                const clonedIcon = new H.map.Icon(iconConfig.url, {
                    size: iconConfig.size
                });
                copy = new H.map.Marker(obj.getGeometry(), {
                    icon: clonedIcon,
                });
                copy.draggable = obj.draggable;
            }

            if (copy) clonedObjects.push(copy);
        });

        group.addObjects(clonedObjects);
        if (clonedCircleOutline) {
            group.addEventListener('tap', () => changeStyle(clonedCircleOutline));
        }

        return group;
    };

    const validate = async () => {
        const newErrors = {...defaultErrors};

        if (!values.zoneName.trim()) {
            newErrors.zoneName = t('errNoName');
        } else if (values.zoneName.length > 50) {
            newErrors.zoneName = t('errNameLength');
        }

        if (!values.address.trim()) {
            newErrors.address = t('errNoAddress');
        }

        const lat = parseFloat(values.latitude);
        if (isNaN(lat) || lat < -90 || lat > 90) {
            newErrors.latitude = t('errLatitude');
        }

        const lng = parseFloat(values.longitude);
        if (isNaN(lng) || lng < -180 || lng > 180) {
            newErrors.longitude = t('errLongitude');
        }

        const rad = parseFloat(values.radius);
        if (isNaN(rad) || rad < 0.01 || rad > 5.0) {
            newErrors.radius = t('errRadius');
        }

        setErrors(newErrors);
        return Object.values(newErrors).every((msg) => msg === '');
    };

    const checkZoneName = (name) => {
        if (zoneNames.includes(name.toLowerCase())) {
            setErrors(prev => ({
                ...prev,
                zoneName: t('errorZoneExists')
            }));
        }
    };

    const onChangeField = (key) => (e) => {
        const value = key === 'radiusSlider' ? e[1] : e.target?.value;
        const stateKey = key === 'radiusSlider' ? 'radius' : key;

        setValues(prev => ({
            ...prev,
            [stateKey]: key === 'radius' ? +value : value
        }));

        setErrors(prev => ({
            ...prev,
            [stateKey]: ''
        }));

        if (stateKey === 'zoneName') {
            checkZoneName(value);
        }

        if (stateKey !== 'address') return;

        if (!value || value.length < 3) {
            setSuggestions([]);
            return;
        }

        const searchService = platform.getSearchService();
        const center = map.getCenter();
        const validTypes = [
            "houseNumber",
            "street",
            "place",
            "locality",
            "park",
            "square",
            "administrativeArea",
            "intersection"
        ];

        searchService.autosuggest(
            {
                q: value,
                at: `${center.lat},${center.lng}`
            },
            result => {
                const filtered = result.items.filter(item =>
                    validTypes.includes(item.resultType) && item.address?.label
                );
                setSuggestions(filtered);
            },
            error => {
                console.error('Autosuggest error:', error);
                setSuggestions([]);
            }
        );
    };

    const handleSuggestionSelect = (item) => {
        const label = item.address.label;
        const { lat, lng } = item.position;

        setValues(prev => ({
            ...prev,
            address: label,
            latitude: lat,
            longitude: lng
        }));

        setSuggestions([]);

        if (item.mapView) {
            const { north, south, east, west } = item.mapView;
            const bounds = new H.geo.Rect(south, west, north, east);
            map.getViewModel().setLookAtData({ bounds });
        } else {
            map.setCenter({ lat, lng });
            map.setZoom(16);
        }
    };

    const cancelForm = () => {
        resetMap();
        const allZonesGroup = map.getObjects().find(
            obj => obj instanceof H.map.Group && obj.getRemoteId() === 'allZonesObject'
        );
        const editedZoneGroup = editedZoneGroupRef.current;
        if (allZonesGroup && editedZoneGroup) {
            allZonesGroup.addObject(editedZoneGroup);
            const polyline = editedZoneGroup.getObjects().find(obj => obj instanceof H.map.Polyline);
            if (polyline) {
                changeStyle(polyline);
            }
        }
        setErrors({...defaultErrors});
        setMode(hasZones ? 'LIST' : 'NO_ZONES');
    }

    const submitZone = async () => {
        const isValid = await validate();
        if (!isValid) return;

        if (mode === 'CREATE') {
            createZone(companyId, {
                ...values,
                user_name: `${user.first_name} ${user.last_name}`,
                user_email: user.email,
            }).then(res => {
                const result = res.data.result;
                if (result === 'OK') {
                    resetMap();
                    getZones(true);
                    toastr.success("", t('successCreatingZone'));
                } else {
                    toastr.error(t('errorCreatingZone'));
                }
            }).catch(err => {
                console.log(err, "create Zone error");
                toastr.error(t('errorCreatingZone'));
            });
        } else if (mode === 'EDIT') {
            editZone(companyId, editedZone.id, values).then(res => {
                const result = res.data.result;
                if (result === 'OK') {
                    resetMap();
                    getZones(true);
                    toastr.success("", t('successEditingZone'));
                } else {
                    toastr.error(t('errorEditingZone'));
                }
            }).catch(err => {
                console.log(err, "edit Zone error");
                toastr.error(t('errorEditingZone'));
            });
        }
    };

    return (
        <div className={`new-zone-form ${className}`}>
            <Form>
                <h3>{mode === 'EDIT' ? t('editZone') : t('createNewZone')}</h3>
                <Form.Group>
                    <Form.Label>{t('zoneName')}</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="e.g., North Lot"
                        value={values.zoneName}
                        isInvalid={!!errors.zoneName}
                        onChange={onChangeField('zoneName')}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.zoneName}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="address-form-group">
                    <Form.Label>{t('address')}</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder={`${t('address')}...`}
                        value={values.address}
                        isInvalid={!!errors.address}
                        onChange={onChangeField('address')}
                        onBlur={() => setTimeout(() => setSuggestions([]), 100)}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.address}
                    </Form.Control.Feedback>
                    {suggestions.length > 0 && (
                        <ul className="suggestion-list">
                            {suggestions.map((item, idx) => (
                                <li
                                    key={idx}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleSuggestionSelect(item)}
                                >
                                    {item.address.label}
                                </li>
                            ))}
                        </ul>
                    )}
                </Form.Group>
                <Form.Group>
                    <Form.Label>{t('latitude')}</Form.Label>
                    <Form.Control
                        type="number"
                        value={values.latitude}
                        isInvalid={!!errors.latitude}
                        onChange={onChangeField('latitude')}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.latitude}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                    <Form.Label>{t('longitude')}</Form.Label>
                    <Form.Control
                        type="number"
                        value={values.longitude}
                        isInvalid={!!errors.longitude}
                        onChange={onChangeField('longitude')}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.longitude}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="radius-form-group">
                    <Form.Label>{`${t('radius')} (${t('miles')})`}</Form.Label>
                    <Row>
                        <Col xs={8} className="radius-slider-column">
                            <RangeSlider
                                min={0.01}
                                max={5.0}
                                step={0.01}
                                className="radius-slider"
                                value={[0, values.radius]}
                                thumbsDisabled={[true, false]}
                                rangeSlideDisabled={true}
                                onInput={onChangeField('radiusSlider')}
                            />
                        </Col>
                        <Col xs={4}>
                            <Form.Control
                                min={0.01}
                                max={5.0}
                                type="number"
                                value={values.radius}
                                isInvalid={!!errors.radius}
                                onChange={onChangeField('radius')}
                            />
                        </Col>
                    </Row>
                    <Form.Control.Feedback className={errors.radius ? 'error-radius' : ''} type="invalid">
                        {errors.radius}
                    </Form.Control.Feedback>
                </Form.Group>
            </Form>
            <div className="zones-form-footer">
                <CustomButton variant="primary-outline" onClick={cancelForm}>{t('btnCancel')}</CustomButton>
                <CustomButton variant="primary" onClick={submitZone}>{t('btnSave')}</CustomButton>
            </div>
        </div>
    )
}
